import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import BookmarksResponseSchema, { BookmarksResponseJSON } from "marketplace/bookmarks/_schema/bookmarks-response-schema";
import BookmarkedCompanySchema, { BookmarkedCompanyJSON } from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-schema";
import BookmarkedIndividualSchema, { BookmarkedIndividualJSON } from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-schema";
import BookmarkedTeamSchema, { BookmarkedTeamJSON } from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-schema";
import { z } from "zod";

export const bookmarksApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Queries
    getAllBookmarks: build.query<BookmarksResponseJSON, void>({
      query: () => "marketplace/bookmarks",
      rawResponseSchema: BookmarksResponseSchema.BookmarksResponseJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    getAllCompanyBookmarks: build.query<BookmarkedCompanyJSON[], void>({
      query: () => "marketplace/company-bookmarks",
      rawResponseSchema: z.array(BookmarkedCompanySchema.BookmarkedCompanyJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    getAllIndividualBookmarks: build.query<BookmarkedIndividualJSON[], void>({
      query: () => "marketplace/individual-bookmarks",
      rawResponseSchema: z.array(BookmarkedIndividualSchema.BookmarkedIndividualJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    getAllTeamBookmarks: build.query<BookmarkedTeamJSON[], void>({
      query: () => "marketplace/team-bookmarks",
      rawResponseSchema: z.array(BookmarkedTeamSchema.BookmarkedTeamJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    // #endregion

    // #region Mutations
    createCompanyBookmark: build.mutation<BookmarkedCompanyJSON, { companyId: string }>({
      query: ({ companyId }) => ({
        url: `marketplace/company-bookmark/${companyId}`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: BookmarkedCompanySchema.BookmarkedCompanyJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    deleteCompanyBookmark: build.mutation<void, { bookmarkId: string }>({
      query: ({ bookmarkId }) => ({
        url: `marketplace/company-bookmark/${bookmarkId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    createIndividualBookmark: build.mutation<BookmarkedIndividualJSON, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individual-bookmark/${individualId}`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: BookmarkedIndividualSchema.BookmarkedIndividualJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    deleteIndividualBookmark: build.mutation<void, { bookmarkId: string }>({
      query: ({ bookmarkId }) => ({
        url: `marketplace/individual-bookmark/${bookmarkId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    createTeamBookmark: build.mutation<BookmarkedTeamJSON, { teamId: string }>({
      query: ({ teamId }) => ({
        url: `marketplace/team-bookmark/${teamId}`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: BookmarkedTeamSchema.BookmarkedTeamJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    deleteTeamBookmark: build.mutation<void, { bookmarkId: string }>({
      query: ({ bookmarkId }) => ({
        url: `marketplace/team-bookmark/${bookmarkId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    // #endregion
  }),
});

export const {
  useGetAllBookmarksQuery,
  useGetAllCompanyBookmarksQuery,
  useGetAllIndividualBookmarksQuery,
  useGetAllTeamBookmarksQuery,
  useCreateCompanyBookmarkMutation,
  useDeleteCompanyBookmarkMutation,
  useCreateIndividualBookmarkMutation,
  useDeleteIndividualBookmarkMutation,
  useCreateTeamBookmarkMutation,
  useDeleteTeamBookmarkMutation,
} = bookmarksApi;
